import React, { useEffect, useState, useRef, useMemo } from 'react';
import { UserResponseType } from '../../models/questionnaire.interface';
import Avatar from 'components/avatar';
import QuestionCard from 'components/question_card';
import UserResponse from 'components/user_response';
import { usePostQuery } from 'api/hooks/usePostQuery';
import { useAtom } from 'jotai';
import EndOfSurveyMessage from 'components/end_of_survey_message';
import {
  singleSelectResponseAtom,
  currentFeedbackQuestionIndexAtom,
  answeredFeedbackQuestionsAtom,
  updateFeedbackSurveyAtom,
  interactionIdAtom,
  feedbackSurveyCompleteAtom,
  decodedTokenAtom,
} from '../../store';
import {
  CreateFeedbackInteractionPostType,
  CreateFeedbackInteractionResponseType,
  UpdateFeedbackInteractionPatchType,
  UpdateFeedbackInteractionResponseType,
  FeedbackSurveyDataType,
  FeedbackQuestionType,
} from '../../models/feedback_survey.interface';
import { Row, Col } from 'antd';
import { usePatchQuery } from 'api/hooks/usePatchQuery';

interface Props {
  recordFeedback(userResponse: UserResponseType): void;
  token: string;
  feedbackSurveyData: FeedbackSurveyDataType;
}

const WEIGHTED_VALUE_EMOJI_KEY = {
  5: '😃',
  4: '🙂',
  3: '😐',
  2: '😣',
  1: '😫',
} as const;
/*
 In TypeScript, adding 'const' here changes the type definition from
  { 5: string, 4: string...}
 to
  { readonly 5: '😃', readonly 4: '🙂'...}
 */

const FeedbackSurvey: React.FC<Props> = ({
  feedbackSurveyData,
  token,
  recordFeedback,
}) => {
  const questions = feedbackSurveyData?.questions;

  const insertEmojis = () => {
    if (feedbackSurveyData.emojisEnabled) {
      const customerSatisfactionQuestion = questions.find(question =>
        question.answers.some(answer => answer?.weightedValue),
      );
      if (customerSatisfactionQuestion) {
        const mappedAnswers = customerSatisfactionQuestion.answers.map(
          (answer) => {
            if (!answer?.weightedValue) return answer;
            return {
              ...answer,
              text: `${
                WEIGHTED_VALUE_EMOJI_KEY[
                  answer.weightedValue as keyof typeof WEIGHTED_VALUE_EMOJI_KEY
                ]
              } ${answer.text}`,
            };
          },
        );
        feedbackSurveyData.questions[
          customerSatisfactionQuestion.index
        ].answers = mappedAnswers;
      }
    }
  };
  useEffect(() => {
    insertEmojis();
  }, []);

  const currentQuestionRef = useRef<null | HTMLDivElement>(null);
  const surveyCompletedRef = useRef<null | HTMLDivElement>(null);
  const [currentFeedbackQuestionIndex, setCurrentFeedbackQuestionIndex]
    = useAtom(currentFeedbackQuestionIndexAtom);
  const [singleSelectResponse, setSingleSelectResponse] = useAtom(
    singleSelectResponseAtom,
  );
  const [answeredFeedbackQuestions] = useAtom(answeredFeedbackQuestionsAtom);
  const [updatedFeedbackSurveyResponse] = useAtom(updateFeedbackSurveyAtom);
  const [interactionId] = useAtom(interactionIdAtom);
  const [decodedToken] = useAtom(decodedTokenAtom);
  const careProviderId = decodedToken?.careProviderId ?? 0;
  const isStaffInteraction = !!decodedToken?.staffMemberId;
  const currentQuestion = questions[currentFeedbackQuestionIndex];
  const [feedbackSurveyComplete, setFeedbackSurveyComplete] = useAtom(
    feedbackSurveyCompleteAtom,
  );
  const [feedbackInteractionId, setFeedbackInteractionId] = useState(0);
  useEffect(() => {
    if (!feedbackSurveyData) setFeedbackSurveyComplete(true);
  }, []);
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };
  const { exec, data: createFeedBackInteractionData } = usePostQuery<
    CreateFeedbackInteractionPostType,
    CreateFeedbackInteractionResponseType
  >('feedback_interactions', config);

  const { exec: updateFeedbackInteraction } = usePatchQuery<
    UpdateFeedbackInteractionPatchType,
    UpdateFeedbackInteractionResponseType
  >(`feedback_interactions/${feedbackInteractionId}`, config);

  useEffect(() => {
    if (feedbackSurveyData?.id && interactionId) {
      exec({
        feedbackScriptId: feedbackSurveyData.id,
        interactionId,
        careProviderId,
      });
    }
  }, []);

  const questionsById = useMemo(() => {
    return feedbackSurveyData.questions.reduce((acc, question) => {
      acc.set(question.id, question);
      return acc;
    }, new Map<number, FeedbackQuestionType>());
  }, [feedbackSurveyData]);

  useEffect(() => {
    if (singleSelectResponse?.answers?.length) {
      const answer = singleSelectResponse.answers[0];
      const question = questionsById.get(singleSelectResponse.questionId);
      const scriptAnswer = question!.answers.find(x => x.id === answer.id);
      if (scriptAnswer!.weightedValue && feedbackSurveyData.emojisEnabled) {
        /*
          Because we have inserted the emojis in the feedbackSurveyData,
          we should parse them from the response, so they are not stored in the
          feedbackInteraction table. Using .slice(3), we ensure we will always remove
          the first two characters of the string - the emoji, and the space between the emoji and the text.
        */
        answer.text = (answer.text || '').slice(3);
      }
      recordFeedback(singleSelectResponse);
      setSingleSelectResponse({ questionId: 0, answers: [] });
    }
  }, [singleSelectResponse]);

  useEffect(() => {
    if (
      updatedFeedbackSurveyResponse.responses.length
      && feedbackInteractionId
    ) {
      updateFeedbackInteraction(updatedFeedbackSurveyResponse);
      getFeedbackQuestionIndex();
    }
  }, [updatedFeedbackSurveyResponse.responses.length, feedbackInteractionId]);

  useEffect(() => {
    if (feedbackSurveyComplete && currentQuestion?.type === 'statement') {
      recordFeedback({ questionId: currentQuestion.id, answers: [] });
    }
    else if (currentQuestion?.type === 'statement') {
      const timer = setTimeout(() => {
        recordFeedback({ questionId: currentQuestion.id, answers: [] });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (createFeedBackInteractionData)
      setFeedbackInteractionId(createFeedBackInteractionData.id);
  }, [createFeedBackInteractionData]);

  const getFeedbackQuestionIndex = () => {
    if (!answeredFeedbackQuestions?.length) return;
    const newIndex = currentFeedbackQuestionIndex + 1;
    if (!feedbackSurveyData.questions[newIndex]) {
      setFeedbackSurveyComplete(true);
    }
    else {
      return setCurrentFeedbackQuestionIndex(newIndex);
    }
  };

  useEffect(() => {
    if (currentQuestionRef?.current) {
      currentQuestionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentFeedbackQuestionIndex]);

  useEffect(() => {
    if (feedbackSurveyComplete && surveyCompletedRef?.current) {
      surveyCompletedRef?.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [feedbackSurveyComplete]);

  const renderPreviousQuestionsAndAnswers = () => {
    return answeredFeedbackQuestions.map((question) => {
      const { answers } = question.userResponse;
      const answerText = answers.map((answer: any) => answer.text).join(', ');
      return (
        <div key={question.id}>
          <div
            style={{
              display: 'flex',
              marginBottom: '20px',
            }}
          >
            <div style={{ marginRight: '12px' }}>
              <Avatar showAvatar={true} />
            </div>
            <QuestionCard
              {...question}
              isBeingEdited={false}
              isAnswered={true}
              isActiveQuestion={false}
            />
          </div>
          {!!answers?.length && (
            <Row justify="end">
              <Col
                style={{
                  maxWidth: '75%',
                  marginBottom: '20px',
                }}
              >
                <UserResponse key={answerText} text={answerText} questionIndex={question.index} />
              </Col>
            </Row>
          )}
        </div>
      );
    });
  };

  const renderCurrentQuestion = () => {
    if (feedbackSurveyComplete) return;
    if (currentFeedbackQuestionIndex > questions.length - 1) return;
    if (
      answeredFeedbackQuestions.some(
        answer => answer.id === currentQuestion.id,
      )
    ) {
      return;
    }

    return (
      <div style={{ display: 'flex', marginBottom: '20px' }}>
        <div style={{ marginRight: '12px' }}>
          <Avatar showAvatar={true} />
        </div>
        <div style={{ width: '100%' }}>
          <QuestionCard
            {...currentQuestion}
            isBeingEdited={false}
            isAnswered={false}
            isActiveQuestion={true}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div>{renderPreviousQuestionsAndAnswers()}</div>
      <div ref={currentQuestionRef}>{renderCurrentQuestion()}</div>
      {feedbackSurveyComplete && (
        <div ref={surveyCompletedRef}>
          <EndOfSurveyMessage isStaffInteraction={isStaffInteraction} />
        </div>
      )}
    </div>
  );
};

export default FeedbackSurvey;
